import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/utils/logger';

const API = '/api';

/**
 * useNearbyRestaurant — Manages browser geolocation, nearby restaurant detection,
 * cooldowns, and dismissals. Only active when `enabled` is true.
 */
export function useNearbyRestaurant(enabled = false) {
  const { authHeaders } = useAuth();
  const [nearbyData, setNearbyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionState, setPermissionState] = useState('prompt'); // prompt | granted | denied
  const [coords, setCoords] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const watchRef = useRef(null);
  const lastCheckRef = useRef(0);

  // Check permission state
  useEffect(() => {
    if (!enabled) return;
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then(result => {
        setPermissionState(result.state);
        result.onchange = () => setPermissionState(result.state);
      }).catch(() => {});
    }
  }, [enabled]);

  // Start watching position when enabled
  useEffect(() => {
    if (!enabled || permissionState === 'denied') return;

    const handlePosition = (position) => {
      try {
        setCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setError(null);
      } catch (e) {
        logger.error('Position handling error:', e);
      }
    };

    const handleError = (err) => {
      if (err.code === 1) setPermissionState('denied');
      setError(err.message);
    };

    try {
      // Get initial position
      navigator.geolocation.getCurrentPosition(handlePosition, handleError, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      });

      // Watch for significant position changes
      watchRef.current = navigator.geolocation.watchPosition(handlePosition, handleError, {
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 120000,
      });
    } catch (e) {
      logger.error('Geolocation API error:', e);
      setPermissionState('denied');
      setError('Geolocation not supported');
    }

    return () => {
      if (watchRef.current !== null) {
        try {
          navigator.geolocation.clearWatch(watchRef.current);
        } catch {}
        watchRef.current = null;
      }
    };
  }, [enabled, permissionState]);

  // Check nearby restaurants when coords change (throttled to every 60 seconds)
  useEffect(() => {
    if (!coords || !enabled || dismissed) return;
    const now = Date.now();
    if (now - lastCheckRef.current < 60000) return;
    lastCheckRef.current = now;

    const checkNearby = async () => {
      setLoading(true);
      try {
        const searchRadius = coords.manual ? Math.round((coords.radiusMi || 3) * 1609.34) : 4828; // default 3 miles
        const res = await axios.post(`${API}/geolocation/nearby`, {
          lat: coords.lat,
          lng: coords.lng,
          radius: searchRadius,
          manual: !!coords.manual,
        }, authHeaders());

        if (res.data.suppressed) {
          setNearbyData(null);
        } else if (res.data.restaurants?.length > 0) {
          setNearbyData(res.data);
        } else {
          setNearbyData(null);
        }
      } catch (err) {
        logger.error('Nearby check failed:', err);
        setNearbyData(null);
      }
      setLoading(false);
    };

    checkNearby();
  }, [coords, enabled, dismissed, authHeaders]);

  const dismiss = useCallback(async (restaurantName) => {
    if (!coords) return;
    setDismissed(true);
    setNearbyData(null);
    try {
      await axios.post(`${API}/geolocation/dismiss`, {
        lat: coords.lat,
        lng: coords.lng,
        restaurant_name: restaurantName || '',
      }, authHeaders());
    } catch (err) {
      logger.error('Dismiss failed:', err);
    }
  }, [coords, authHeaders]);

  const requestPermission = useCallback(() => {
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          try {
            setPermissionState('granted');
            setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
          } catch (e) {
            logger.error('Position set error:', e);
          }
        },
        (err) => {
          if (err.code === 1) setPermissionState('denied');
          setError(err.message);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } catch (e) {
      logger.error('requestPermission error:', e);
      setPermissionState('denied');
      setError('Geolocation not available');
    }
  }, []);

  const setManualCoords = useCallback((lat, lng, radiusMi = 3) => {
    setCoords({ lat, lng, accuracy: 0, manual: true, radiusMi });
    setPermissionState('granted');
    setError(null);
  }, []);

  return {
    nearbyData,
    loading,
    error,
    permissionState,
    coords,
    dismissed,
    dismiss,
    requestPermission,
    setManualCoords,
  };
}
